import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { SparklesIcon, XMarkIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { useIntl } from 'react-intl';
import { useSuccessionTheme } from '../SuccessionThemeProvider';
import AIAssistantChat from './AIAssistantChat';

/**
 * AIAssistantWidget - Floating AI assistant widget inspired by the tax assistant
 * Features:
 * - Beautiful floating trigger button
 * - Full-screen modal with dark theme
 * - Integration with existing ChatPanel infrastructure
 * - Agent-generated conversation starters
 * - Smooth animations and modern UI
 */
const AIAssistantWidget = ({ currentUser, integrated = false, className = "" }) => {
  const intl = useIntl();
  const { theme } = useSuccessionTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [suggestedConversations, setSuggestedConversations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const widgetRef = useRef(null);
  
  // Load suggested conversations from backend
  useEffect(() => {
    const loadSuggestions = async () => {
      if (suggestedConversations.length > 0) return;
      
      // Always set fallback suggestions first
      const fallbackSuggestions = [
        {
          id: 'succession_status',
          title: "What's the status of my succession plan?",
          description: "Get an overview of your succession planning progress and next steps",
          icon: "📋",
          category: "planning"
        },
        {
          id: 'practice_value',
          title: "What's my practice worth?",
          description: "Get insights into your practice valuation and key value drivers",
          icon: "💰",
          category: "valuation"
        },
        {
          id: 'bcp_help',
          title: "Help me create a business continuity plan",
          description: "Get guided assistance to build your BCP step by step",
          icon: "🛡️",
          category: "continuity"
        },
        {
          id: 'find_successors',
          title: "Show me potential successors in my area",
          description: "Find qualified advisors who might be interested in your practice",
          icon: "👥",
          category: "succession"
        },
        {
          id: 'compare_peers',
          title: "How does my practice compare to peers?",
          description: "Get benchmarks and comparisons with similar practices",
          icon: "📊",
          category: "analysis"
        },
        {
          id: 'quarterly_tasks',
          title: "What tasks should I focus on this quarter?",
          description: "Get prioritized action items for your succession planning",
          icon: "✅",
          category: "planning"
        }
      ];
      
      // Set fallback suggestions immediately so UI renders
      setSuggestedConversations(fallbackSuggestions);
      setIsLoading(false);
      
      // Try to load from backend as enhancement (optional)
      try {
        const response = await fetch('/ai_assistant/suggestions', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          }
        });

        const data = await response.json();
        
        if (data.success && data.suggestions && data.suggestions.length > 0) {
          setSuggestedConversations(data.suggestions);
        }
      } catch (error) {
        console.warn('Could not load suggestions from backend, using fallbacks:', error);
        // Fallback suggestions are already set above
      }
    };

    loadSuggestions();
  }, [suggestedConversations.length]);

  const handleOpen = () => {
    setIsOpen(true);
    setIsMinimized(false);
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsMinimized(false);
    setSelectedConversation(null);
  };

  const handleMinimize = () => {
    setIsMinimized(true);
  };

  const handleStartConversation = (suggestion) => {
    setSelectedConversation(suggestion);
    console.log('Starting conversation with:', suggestion);
  };

  // Floating trigger button
  const TriggerButton = () => (
    <button
      ref={widgetRef}
      onClick={handleOpen}
      className={`ai-assistant-trigger ${className}`}
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        width: '64px',
        height: '64px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 50%, #1e40af 100%)',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 8px 32px rgba(59, 130, 246, 0.4), 0 4px 16px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        zIndex: 1000,
        overflow: 'hidden'
      }}
      onMouseEnter={(e) => {
        e.target.style.transform = 'scale(1.1) rotate(-5deg)';
        e.target.style.boxShadow = '0 12px 40px rgba(59, 130, 246, 0.6), 0 8px 24px rgba(0, 0, 0, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.target.style.transform = 'scale(1) rotate(0deg)';
        e.target.style.boxShadow = '0 8px 32px rgba(59, 130, 246, 0.4), 0 4px 16px rgba(0, 0, 0, 0.1)';
      }}
    >
      {/* Sparkle animation background */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'radial-gradient(circle at 30% 40%, rgba(255, 255, 255, 0.3) 0%, transparent 50%), radial-gradient(circle at 70% 70%, rgba(255, 255, 255, 0.2) 0%, transparent 50%)',
        borderRadius: '50%'
      }} />
      
      <SparklesIcon 
        style={{
          width: '28px',
          height: '28px',
          color: 'white',
          position: 'relative',
          zIndex: 1,
          filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3))'
        }}
      />
      
      {/* Pulse ring */}
      <div style={{
        position: 'absolute',
        inset: '-8px',
        borderRadius: '50%',
        border: '2px solid rgba(59, 130, 246, 0.3)',
        animation: 'pulse-ring 2s infinite'
      }} />
    </button>
  );

  // Modal interface - use useMemo to prevent recreation
  const modal = useMemo(() => {
    if (!isOpen) return null;

    return createPortal(
      <div className="fixed inset-0 bg-black/40 z-50">
        <div className="w-full h-full flex items-center justify-center p-4">
        <div className="bg-succession-bg-primary rounded-xl shadow-xl border border-succession-border flex flex-col overflow-hidden" style={{ width: '66vw', height: '66vh' }}>
          {/* Header */}
          <div className="px-4 py-2 border-b border-succession-border flex items-center justify-between">
            <h3>{intl.formatMessage({ id: 'succession.agent_system.ai_assistant_widget.title' })}</h3>
            
            <button onClick={handleClose} className="text-succession-text-secondary hover:text-succession-text-primary">✕</button>
          </div>

          {/* Content */}
          <div className="flex-1 flex overflow-hidden">
            <AIAssistantChat
              key={`chat-${isOpen ? 'open' : 'closed'}`}
              currentUser={currentUser}
              suggestedConversations={suggestedConversations}
              onStartConversation={handleStartConversation}
              selectedConversation={selectedConversation}
              onConversationStarted={() => setSelectedConversation(null)}
              isLoading={isLoading}
              isMinimized={isMinimized}
            />
          </div>
        </div>
        </div>
      </div>,
      document.body
    );
  }, [isOpen, currentUser, suggestedConversations, selectedConversation, isLoading, isMinimized]);

  // If integrated mode, render as dashboard widget
  if (integrated) {
    return (
      <>
        <div className={`bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg h-full transition-all duration-300 hover:shadow-succession-lg hover:scale-105 transform border border-succession-border ${className}`}>
          <div className="px-4 py-5 sm:p-6 h-full flex flex-col">
            <div className="mb-3">
              <h3>{intl.formatMessage({ id: 'succession.agent_system.ai_assistant_widget.title' })}</h3>
            </div>
            <div className="flex-1 flex items-center justify-center">
              {isLoading ? (
                <div className="flex flex-wrap gap-2 items-center justify-center">
                  {[1, 2, 3, 4, 5, 6].map(i => (
                    <div key={i} className="px-4 py-2 rounded-full text-xs bg-succession-bg-secondary border border-succession-border animate-pulse">
                      {intl.formatMessage({ id: 'succession.agent_system.ai_assistant_widget.loading_skeleton' })}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2 items-center justify-center text-center">
                  {suggestedConversations.slice(0, 6).map((suggestion) => (
                    <button
                      key={suggestion.id}
                      onClick={() => {
                        handleStartConversation(suggestion);
                        handleOpen();
                      }}
                      className="px-4 py-2 rounded-full text-xs bg-succession-bg-secondary text-succession-text-secondary hover:bg-succession-hover-bg border border-succession-border"
                    >
                      {suggestion.title}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="mt-3 pt-2 mt-auto">
              <div className="mt-2">
                <input
                  onFocus={() => handleOpen()}
                  placeholder={intl.formatMessage({ id: 'succession.agent_system.ai_assistant_widget.ask_placeholder' })}
                  readOnly
                  className="w-full border border-succession-border rounded-lg px-3 py-2 bg-succession-bg-primary text-succession-text-primary placeholder-succession-text-secondary cursor-text"
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Modal - Always render regardless of mode */}
        {modal}
      </>
    );
  }

  // Otherwise render floating widget
  return (
    <>
      <TriggerButton />
      {modal}
      
      {/* CSS Animations */}
      <style jsx global>{`
        @keyframes pulse-ring {
          0% {
            transform: scale(1);
            opacity: 0.8;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.4;
          }
          100% {
            transform: scale(1);
            opacity: 0.8;
          }
        }
        
        .ai-assistant-trigger:active {
          transform: scale(0.95) !important;
        }
      `}</style>
    </>
  );
};

export default AIAssistantWidget;